import React, { useEffect, useState } from "react";
import ECLFormInputs from "./ECLFormInputs";
import useSelectorId from "../../../../utilities/hooks/useSelectorId";
import debounce from "../../../../utilities/debounce";
import sourceOneRestRequest from "../../../../utilities/requests/SourceOneRestRequest";
import querySeanQL from "../../../../utilities/requests/SeanQL";
import { ECLUnsafeStringifyULID } from "../../../../utilities/ECLUUID";
import { ECLLoader } from "../../../index";
import "./ECLFormWrapper.css";

export default function FormWrapper(props) {
   const [loading, setLoading] = useState(true);
   const error = null;
   const wrapperID = useSelectorId();
   //Debounce the signal that let's us know when someone has submitted something into input, but hasn't touched it in a couple seconds
   const signalInput = debounce(() => {
      const formElement = document.querySelector(`#form-block-${wrapperID}`); //get form element
      formElement.dispatchEvent(new CustomEvent("submitSignal")); //dispatch ready for submit event
   });

   function prepareForm(form, object, links) {
      form.properties.forEach((p) => {
         if (p.ownsData) {
            if (p.type.backlink) {
               object[p.UUID] = [];
               links
                  .find((l) => l.parent === p.internalID)
                  .instances.forEach((l, i) => {
                     object[p.UUID].push({});
                     prepareForm(p.link, object[p.UUID][i]);
                  });
            } else {
               object[p.UUID] = {};
               prepareForm(p.link, object[p.UUID]);
            }
         } else {
            object[p.UUID] = null;
         }
      });
   }

   //Returns the nested object you need to update the value
   function gimmeThatObjectINeedToUpdate(parentObject, formObject) {
      let possibles = Object.values(formObject)
         .map((v) => {
            if (v && typeof v === "object") {
               if (Array.isArray(v)) {
                  if (v.length !== 0 && typeof v[0] === "object") {
                     return v;
                  }
                  //Else it's a select and we can ignore
               } else {
                  if (Object.values(v).some((val) => typeof val === "object")) {
                     return v;
                  }
               }
            }
            return null;
         })
         .filter((v) => v !== null)
         .flat();

      // if I can find the parent object in the possible pile, then I should just return that object early, otherwise it's time
      // to recursively iterate through the possibles.
      const shortCircuit = possibles.find((p) =>
         Object.entries(p).find((k) => k === parentObject),
      );
      if (shortCircuit) {
         return shortCircuit;
      }
   }

   //When "readyForSubmit" event occurs, do the thing with the current state
   const handleSignal = async (e) => {
      let formObject = {};
      prepareForm(props.form, formObject, props.links);
      let key_vals = [];
      let promises = [];

      for await (const target of e.target) {
         if (target.tagName === "INPUT" || target.tagName == "TEXTAREA") {
            if (!target.closest("fieldset")) {
               if (target.files?.length > 0) {
                  let value = target.files[0].arrayBuffer().then((res) => {
                     formObject[target.id.split("--").at(-1)] = res;
                  });
                  promises.push(Promise.resolve(value));
               } else {
                  formObject[target.id.split("--").at(-1)] =
                     target.type === "number" || target.type === "range"
                        ? Number(target.value)
                        : target.value;
               }
            } else {
               const ancestorField = target.closest("fieldset");
               const parentObject = ancestorField.dataset.object;
               const instanceCount = ancestorField.dataset.count;
               const propertyUUID = target.id.split("--").at(-1);

               if (Object.keys(formObject).includes(parentObject)) {
                  //So if we're lucky, the parent object is a root link, which means we can use the above
                  //variables to place the value in the object
                  if (target.files?.length > 0) {
                     if (formObject[parentObject].length) {
                        let value = target.files[0]
                           .arrayBuffer()
                           .then((res) => {
                              formObject[parentObject][instanceCount][
                                 propertyUUID
                              ] = res;
                           });
                        promises.push(Promise.resolve(value));
                     } else {
                        let value = target.files[0]
                           .arrayBuffer()
                           .then((res) => {
                              formObject[parentObject][propertyUUID] = res;
                           });
                        promises.push(Promise.resolve(value));
                     }
                  } else {
                     if (formObject[parentObject].length) {
                        formObject[parentObject][instanceCount][propertyUUID] =
                           target.type === "number" || target.type === "range"
                              ? Number(target.value)
                              : target.value;
                     } else {
                        formObject[parentObject][propertyUUID] =
                           target.type === "number" || target.type === "range"
                              ? Number(target.value)
                              : target.value;
                     }
                  }
               } else {
                  //If we're not lucky, time to go shopping
                  let ourObject = gimmeThatObjectINeedToUpdate(
                     parentObject,
                     formObject,
                  );
                  if (target.files?.length > 0) {
                     if (ourObject.length) {
                        let value = target.files[0]
                           .arrayBuffer()
                           .then((res) => {
                              ourObject[parentObject][instanceCount][
                                 propertyUUID
                              ] = res;
                           });
                        promises.push(Promise.resolve(value));
                     } else {
                        let value = target.files[0]
                           .arrayBuffer()
                           .then((res) => {
                              ourObject[parentObject][propertyUUID] = res;
                           });
                        promises.push(Promise.resolve(value));
                     }
                  } else {
                     if (ourObject.length) {
                        ourObject[parentObject][instanceCount][propertyUUID] =
                           target.type === "number" || target.type === "range"
                              ? Number(target.value)
                              : target.value;
                     } else {
                        ourObject[parentObject][propertyUUID] =
                           target.type === "number" || target.type === "range"
                              ? Number(target.value)
                              : target.value;
                     }
                  }
               }
            }

            if (target.files?.length > 0) {
               let value = target.files[0].arrayBuffer().then((res) => {
                  key_vals.push({
                     key: target.id,
                     value: res,
                  });
               });
               promises.push(Promise.resolve(value));
            } else {
               key_vals.push({
                  key: target.id,
                  value: target.value,
               });
            }
         }
      }
      props.links
         .filter((l) => l.selectState)
         .forEach((l) => {
            key_vals.push({
               key: l.parent,
               value: l.selectState
                  .filter((ll) => ll.selected)
                  .map((ll) => ll.searchableString),
            });
            if (l.root) {
               let propID = props.form.properties.find(
                  (p) => p.internalID === l.parent,
               ).UUID;
               formObject[propID] = l.selectState
                  .filter((ll) => ll.selected)
                  .map((ll) => ll.searchableString);
            } else {
               //TODO: GROSS STUFF
               //Need to find the property ID UUID (possibly nested), then update the associated object with the selectState
            }
         });
      Promise.allSettled(promises).then(() => {
         sourceOneRestRequest({
            query_type: "app_session_set_state",
            session_id: props.session,
            block_key: props.blockID,
            key_value_pairs: key_vals,
         }).catch((error) => console.error(error));
      });
   };

   useEffect(() => {
      let params = [];
      //Populate select link lists here
      props.links
         .filter((l) => l.selectState)
         .forEach((l) =>
            params.push(
               `'${l.queryable}'.project('${l.displayProperty ? l.displayProperty : "id"}')`,
            ),
         );
      Promise.all(params.map((p) => querySeanQL(p))).then((res) => {
         const selectableLinks = props.links.filter((l) => l.selectState);
         const updatedLinks = selectableLinks.map((u, i) => {
            return {
               ...u,
               selectState: res[i].rows.map((r) => {
                  return {
                     id:
                        res[i].columns[0].metadata.get(
                           "ARROW:extension:name",
                        ) === "ecl.uuid"
                           ? ECLUnsafeStringifyULID(r[0])
                           : String(r[0]),
                     searchableString:
                        res[i].columns[0].metadata.get(
                           "ARROW:extension:name",
                        ) === "ecl.uuid"
                           ? ECLUnsafeStringifyULID(r[0])
                           : String(r[0]),
                     jsx:
                        res[i].columns[0].metadata.get(
                           "ARROW:extension:name",
                        ) === "ecl.uuid"
                           ? ECLUnsafeStringifyULID(r[0])
                           : String(r[0]),
                     selected: false,
                  };
               }),
            };
         });
         props.setLinks(
            props.links.map((l) => {
               const updatedLink = updatedLinks.find(
                  (u) => u.parent === l.parent,
               );
               if (updatedLink) {
                  return updatedLink;
               }
               return l;
            }),
         );
         setLoading(false);
      });
   }, [props.form]);

   useEffect(() => {
      //Hook up debouncer listeners
      const formElem = document.querySelector(`#form-block-${wrapperID}`);
      // submit handler
      if (formElem) {
         formElem.addEventListener("input", signalInput);
         formElem.addEventListener("submitSignal", handleSignal);
      }
      return () => {
         if (formElem) {
            formElem.removeEventListener("input", signalInput);
            formElem.removeEventListener("submitSignal", handleSignal);
         }
      };
   }, [loading, props.links, props.form]); //guh, need to track anything you use in the handleSignal function

   if (loading || error) {
      return <ECLLoader message={error} />;
   }

   return (
      <form className="ecl-block-form" id={`form-block-${wrapperID}`}>
         {/* <label className="form-block-ontology-label">{props.form.name}</label> */}
         <ECLFormInputs
            object={props.form}
            links={props.links}
            setLinks={props.setLinks}
            lineage={props.form.UUID}
            sessionState={props.sessionState}
         />
      </form>
   );
}
